import { useRef, useState } from "react";
import { ImagePlus, Loader2, X } from "lucide-react";

import { Img } from "./ui.tsx";
import { cn } from "@/lib/utils";

/**
 * ═══════════════════════════════════════════════════════════════
 *  图片上传
 * ═══════════════════════════════════════════════════════════════
 *
 * 走**代理上传**：图片经 API 转存进对象存储。
 * ⚠️ 本来想做预签名直传（省 API 带宽），但 MinIO 的 S3 接口不实现
 *    `PutBucketCors`，浏览器跨域 PUT 会被挡下 —— 配 CORS 要改 MinIO 服务端配置，
 *    那会变成换台机器就得重做的部署前置条件。代理上传还顺带能校验真实文件头。
 *
 * 🔴 **每张图独立成功/失败。** 一次选了 9 张，第 5 张挂了不能让前 4 张也白传 ——
 *    建材供应商传的是几 MB 的实拍图，重来一次代价很大。
 *
 * ⚠️ 传上去的图 `refCount` 是 0（还没被任何商品引用）——
 *    用户填完表单不提交就走了的话，清理任务过几天会把它扫掉。
 */

export type UploadScope = "product" | "case" | "shop" | "license" | "proof";

/** 各用途的大小上限，跟服务端保持一致（服务端才是最后一道）。 */
const MAX_BYTES: Record<UploadScope, number> = {
  product: 8 * 1024 * 1024,
  case: 8 * 1024 * 1024,
  shop: 2 * 1024 * 1024,
  license: 12 * 1024 * 1024,
  proof: 12 * 1024 * 1024,
};

/** 一张图在界面上的状态。 */
interface Slot {
  /** 本地预览用的 blob 地址 —— 传完之前先让用户看到东西。 */
  preview: string;
  objectKey?: string;
  url?: string;
  error?: string;
  uploading: boolean;
}

async function uploadOne(
  file: File,
  scope: UploadScope,
  orgId: number | undefined,
): Promise<{ objectKey: string; url: string }> {
  /**
   * ⚠️ 大小在本地先拦一道 —— 传完再被服务端拒等于白白浪费用户的流量，
   *    而工地上很多人用的是手机流量。
   *    但**服务端那一道不能省**：本地这道绕得过去（改前端代码就行）。
   */
  const max = MAX_BYTES[scope];
  if (file.size > max) {
    throw new Error(
      `图太大了（${(file.size / 1024 / 1024).toFixed(1)}MB，最多 ${max / 1024 / 1024}MB）`,
    );
  }

  const form = new FormData();
  form.append("scope", scope);
  if (orgId) form.append("orgId", String(orgId));
  /** ⚠️ 文件字段必须放最后 —— fastify-multipart 按顺序解析，
   *     文件之前的普通字段才进得了 `file.fields`。 */
  form.append("file", file, file.name);

  /**
   * ⚠️ **不能设 Content-Type** —— 浏览器要自己加 multipart 的 boundary，
   *    手写会让服务端解析不出分段。
   */
  const res = await fetch("/api/media/upload", {
    method: "POST",
    credentials: "include",
    body: form,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { message?: string } | null;
    throw new Error(body?.message ?? `上传失败（${res.status}）`);
  }
  return (await res.json()) as { objectKey: string; url: string };
}

export function ImageUpload({
  value,
  onChange,
  scope,
  orgId,
  max = 9,
  label = "添加图片",
  hint,
  variant = "grid",
}: {
  /** 已有的对象 key 列表。⚠️ 存 key 不存 url —— url 会随域名/存储变。 */
  value: string[];
  onChange: (keys: string[]) => void;
  scope: UploadScope;
  /** 以哪个商家的身份传。平台传 undefined。 */
  orgId?: number;
  max?: number;
  label?: string;
  hint?: string;
  /**
   * 第一张是不是「主图」。
   *
   * ⚠️ **详情图要传 `false`。** 详情图是一串**按顺序竖着排**的长图，
   *    没有"主图"这回事 —— 在第一张上盖个「主图」只会让人以为它会被拿去当封面。
   *    那一格改成显示**序号**：详情页就是按这个顺序往下排的，
   *    顺序才是他在这儿唯一要确认的信息。
   */
  /**
   * 怎么摆这些图。
   *
   * - `grid`（默认）：80px 的方格。**商品图**用它 —— 商品图本来就是方图，
   *   一排能摆下六张，一眼看全。
   * - `strip`：**整张铺开、竖着一路排下来**，就是详情页真正的样子。
   *   **详情图用它。**
   *
   * 🔴 详情图不能用方格。详情图是 750×3000 这种长图，缩进 80px 的方块里
   *    只剩一条糊掉的色带 —— 他传完根本认不出哪张是哪张，更看不出顺序对不对，
   *    而顺序恰恰是详情图唯一要确认的东西。铺开之后这一格就是详情页的预览：
   *    传完立刻看到客户会看到什么。
   */
  variant?: "grid" | "strip";
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [slots, setSlots] = useState<Slot[]>([]);

  const total = value.length + slots.filter((s) => s.uploading || s.error).length;

  async function pick(files: FileList | null) {
    if (!files || files.length === 0) return;
    const room = max - value.length;
    const chosen = Array.from(files).slice(0, room);

    /** 先把占位放上去，用户立刻看到反馈。 */
    const started: Slot[] = chosen.map((f) => ({
      preview: URL.createObjectURL(f),
      uploading: true,
    }));
    setSlots((prev) => [...prev, ...started]);

    const done: string[] = [];
    await Promise.all(
      chosen.map(async (file, i) => {
        const slot = started[i]!;
        try {
          const r = await uploadOne(file, scope, orgId);
          done.push(r.objectKey);
          setSlots((prev) =>
            prev.map((s) =>
              s === slot ? { ...s, uploading: false, objectKey: r.objectKey, url: r.url } : s,
            ),
          );
        } catch (err) {
          /** 🔴 单张失败不影响别的 —— 把错挂在这张图上让他单独重试。 */
          setSlots((prev) =>
            prev.map((s) =>
              s === slot
                ? { ...s, uploading: false, error: err instanceof Error ? err.message : "上传失败" }
                : s,
            ),
          );
        }
      }),
    );

    if (done.length > 0) onChange([...value, ...done]);
    /** 成功的从临时槽里撤掉 —— 它们已经进 value 了，留着会显示两遍。 */
    setSlots((prev) => prev.filter((s) => s.error || s.uploading));
    if (inputRef.current) inputRef.current.value = "";
  }

  /**
   * ═══════════════════════════════════════════════════════════════
   *  铺开版（详情图）—— 这一格就是**详情页的预览**
   * ═══════════════════════════════════════════════════════════════
   *
   * 🔴 传完立刻看到客户会看到什么。方格版做不到这件事：详情图是
   *    750×3000 这种长图，缩进 80px 的方块只剩一条糊掉的色带 ——
   *    他认不出哪张是哪张，也看不出顺序对不对。
   *
   * ⚠️ **图之间不留缝、不加圆角**：详情页上它们本来就是一张接一张顶着排的，
   *    这里留缝的话预览跟真实页面对不上，等于预览了个假的。
   * ⚠️ 删除按钮压在**右上角**（老板指定），半透明底 + 白叉，
   *    浅色图深色图上都看得见。
   */
  if (variant === "strip") {
    return (
      <div>
        <div className="overflow-hidden rounded-lg border border-border bg-secondary/30">
          {value.length === 0 && slots.length === 0 && (
            <p className="px-4 py-8 text-center text-[13px] text-muted-foreground">
              还没有详情图。加上去之后，这里显示的就是客户在详情页最下面看到的样子。
            </p>
          )}

          {value.map((key, i) => (
            <div key={key} className="relative block">
              {/*
                ⚠️ 用原生 `<img>` 不是 `Img`：`Img` 是按**缩略图**设计的
                   （固定方块占位 + 兜底图），长图套上去会先撑出一个方洞再跳变。
                ⚠️ 不设高度、不裁剪 —— 详情图切一刀就把中间的说明文字切没了。
              */}
              <img
                src={`/api/media/view?key=${encodeURIComponent(key)}`}
                alt={`详情图 ${i + 1}`}
                className="block w-full"
              />
              <span className="absolute top-2 left-2 rounded bg-foreground/55 px-1.5 py-0.5 text-[11px] text-white">
                第 {i + 1} 张
              </span>
              <button
                type="button"
                className="absolute top-2 right-2 flex size-7 items-center justify-center rounded-full bg-foreground/55 text-white transition-colors hover:bg-destructive"
                onClick={() => onChange(value.filter((k) => k !== key))}
                aria-label={`删除第 ${i + 1} 张详情图`}
              >
                <X className="size-4" />
              </button>
            </div>
          ))}

          {slots.map((s, i) => (
            <div key={i} className="relative block">
              <img src={s.preview} alt="" className="block w-full opacity-40" />
              <div className="absolute inset-0 flex items-center justify-center">
                {s.uploading ? (
                  <span className="flex items-center gap-2 rounded-full bg-background/90 px-3 py-1.5 text-[13px] text-muted-foreground">
                    <Loader2 className="size-4 animate-spin" />
                    上传中…
                  </span>
                ) : (
                  <button
                    type="button"
                    className="rounded-full bg-background/90 px-3 py-1.5 text-[13px] text-destructive"
                    onClick={() => setSlots((prev) => prev.filter((x) => x !== s))}
                  >
                    {s.error} · 点掉
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {total < max && (
          <button
            type="button"
            className="mt-2 flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border text-[14px] text-muted-foreground transition-colors hover:border-brand-600 hover:text-brand-700 active:bg-secondary"
            onClick={() => inputRef.current?.click()}
          >
            <ImagePlus className="size-4" />
            {label}
          </button>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          className="hidden"
          onChange={(e) => void pick(e.target.files)}
        />

        <p className={cn("mt-1.5 text-[11px] text-muted-foreground", !hint && "hidden")}>{hint}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {value.map((key, i) => (
          <div key={key} className="relative">
            <Img
              src={`/api/media/view?key=${encodeURIComponent(key)}`}
              alt={`图 ${i + 1}`}
              className="size-20 rounded-lg border border-border object-cover"
            />
            {/*
              ⚠️ 删除按钮做成 24px 的小圆点压在角上 —— 手机上手指会挡住图，
                 但它离图的边缘足够远，不至于误触。
            */}
            <button
              type="button"
              className="absolute -top-1.5 -right-1.5 flex size-6 items-center justify-center rounded-full bg-foreground/80 text-white active:opacity-70"
              onClick={() => onChange(value.filter((k) => k !== key))}
              aria-label="删除这张图"
            >
              <X className="size-3.5" />
            </button>
            {/* 第一张是主图 —— 列表和分享卡片用的都是它，要让用户知道。 */}
            {i === 0 && (
              <span className="absolute bottom-0 inset-x-0 rounded-b-lg bg-foreground/60 py-0.5 text-center text-[10px] text-white">
                主图
              </span>
            )}
          </div>
        ))}

        {slots.map((s, i) => (
          <div
            key={i}
            className="relative size-20 overflow-hidden rounded-lg border border-border"
          >
            <img src={s.preview} alt="" className="size-full object-cover opacity-40" />
            <div className="absolute inset-0 flex items-center justify-center">
              {s.uploading ? (
                <Loader2 className="size-5 animate-spin text-muted-foreground" />
              ) : (
                <button
                  type="button"
                  className="px-1 text-[10px] leading-tight text-destructive"
                  onClick={() => setSlots((prev) => prev.filter((x) => x !== s))}
                >
                  {s.error}
                  <span className="mt-0.5 block underline">点掉</span>
                </button>
              )}
            </div>
          </div>
        ))}

        {total < max && (
          <button
            type="button"
            className="flex size-20 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-border text-muted-foreground active:bg-secondary"
            onClick={() => inputRef.current?.click()}
          >
            <ImagePlus className="size-5" />
            <span className="text-[11px]">{label}</span>
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple
        className="hidden"
        onChange={(e) => void pick(e.target.files)}
      />

      <p className={cn("mt-1.5 text-[11px] text-muted-foreground", !hint && "hidden")}>{hint}</p>
    </div>
  );
}
